import { RowData, RowModel } from "@tanstack/react-table";
import React from "react";
import { useStore } from "../../../stores/store";

type Props<T extends RowData> = {
  getSelectedRowModel: () => RowModel<T>;
  toggleAllRowsSelected: () => void;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  nextPage: () => void;
  pageCount: number;
  pageIndex: number;
  pageSize: number;
  previousPage: () => void;
  rerender: () => void;
  rowSelection: Object;
  setPageIndex: (index: number) => void;
  setPageSize: (size: number) => void;
  totalRows: number;
  includeSelection: boolean;
  includePagination: boolean;
};

export function ActionButtons<T extends RowData>({
  getSelectedRowModel,
  toggleAllRowsSelected,
  hasNextPage,
  hasPreviousPage,
  nextPage,
  pageCount,
  pageIndex,
  pageSize,
  previousPage,
  setPageIndex,
  totalRows,
  includeSelection,
  includePagination,
}: Props<T>) {
  const selectedRowModel = getSelectedRowModel();
  const {
    exportSelectedResults,
    removeUnselectedResults,
    reports,
    selectedResults,
  } = useStore();
  const buttonClasses = "btn btn-sm mx-1 btn-outline flex items-center";
  return (
    <>
      {includePagination && (
        <>
          <div className="flex items-center justify-between rounded-b-lg border border-[#4F4E4E] bg-[#4F4E4E] text-white font-bold p-1 text-sm">
            <span className="flex items-center gap-1">
              <strong>
                {pageIndex * pageSize + 1} -{" "}
                {Math.min((pageIndex + 1) * pageSize, totalRows)} of {totalRows}{" "}
                Result{totalRows === 1 ? "" : "s"}
              </strong>
            </span>
            <div className="flex items-center">
              <button
                className={hasPreviousPage ? "" : " opacity-50"}
                onClick={() => setPageIndex(0)}
                disabled={!hasPreviousPage}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="1em"
                  height="1em"
                  viewBox="0 0 56 56"
                >
                  <path
                    fill="currentColor"
                    d="M25.328 47.723c.399.398.914.633 1.524.633c1.218 0 2.133-.915 2.133-2.133c0-.586-.235-1.125-.61-1.524L11.313 28.012l17.062-16.688c.375-.398.61-.96.61-1.523c0-1.219-.915-2.157-2.133-2.157c-.61 0-1.125.235-1.524.633L6.766 26.441c-.446.375-.703.961-.703 1.57c0 .587.257 1.126.703 1.571Zm20.93 0c.422.398.937.633 1.523.633c1.219 0 2.156-.915 2.156-2.133c0-.586-.234-1.125-.609-1.524L32.266 28.012l17.062-16.688c.375-.398.61-.96.61-1.523a2.121 2.121 0 0 0-2.157-2.157a2.09 2.09 0 0 0-1.5.633L27.695 26.441c-.445.375-.68.961-.68 1.57c0 .587.235 1.126.68 1.571Z"
                  ></path>
                </svg>
              </button>
              <button
                className={"mr-1 " + (hasPreviousPage ? "" : " opacity-50")}
                onClick={() => previousPage()}
                disabled={!hasPreviousPage}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="1em"
                  height="1em"
                  viewBox="0 0 56 56"
                >
                  <path
                    fill="currentColor"
                    d="M35.805 47.723c.398.398.914.633 1.523.633c1.219 0 2.133-.915 2.133-2.133c0-.586-.234-1.125-.61-1.524L21.79 28.012l17.063-16.688c.375-.398.609-.96.609-1.523c0-1.219-.914-2.157-2.133-2.157c-.61 0-1.125.235-1.523.633L17.242 26.441c-.445.375-.703.961-.703 1.57c0 .587.258 1.126.703 1.571Z"
                  ></path>
                </svg>
              </button>
              <span className="flex flex-1">
                {pageIndex - 3 > 0 && (
                  <>
                    <span
                      className="ml-1 cursor-pointer hover:underline"
                      onClick={() => setPageIndex(0)}
                    >
                      1
                    </span>
                    <span className="ml-1 opacity-50">...</span>
                  </>
                )}
                {pageIndex - 1 > 0 && pageIndex - 1 ? (
                  <span
                    className="ml-1 cursor-pointer hover:underline"
                    onClick={() => setPageIndex(pageIndex - 2)}
                  >
                    {pageIndex - 1 > 0 && pageIndex - 1}
                  </span>
                ) : null}

                {pageIndex > 0 && pageIndex ? (
                  <span
                    className="ml-1 cursor-pointer hover:underline"
                    onClick={() => setPageIndex(pageIndex - 1)}
                  >
                    {pageIndex > 0 && pageIndex}
                  </span>
                ) : null}

                <span className="text-[#aaa] ml-1">{pageIndex + 1}</span>

                {pageIndex + 1 < pageCount && pageIndex + 1 && (
                  <span
                    className="ml-1 cursor-pointer hover:underline"
                    onClick={() => setPageIndex(pageIndex + 1)}
                  >
                    {pageIndex + 2 < pageCount && pageIndex + 2}
                  </span>
                )}

                {pageIndex + 2 < pageCount && pageIndex + 2 && (
                  <span
                    className="ml-1 cursor-pointer hover:underline"
                    onClick={() => setPageIndex(pageIndex + 2)}
                  >
                    {pageIndex + 3 < pageCount && pageIndex + 3}
                  </span>
                )}

                {pageIndex + 3 < pageCount && pageIndex + 3 && (
                  <>
                    <span className="ml-1 opacity-50">...</span>
                    <span
                      className="ml-1 cursor-pointer hover:underline"
                      onClick={() => setPageIndex(pageCount - 1)}
                    >
                      {pageCount}
                    </span>
                  </>
                )}
              </span>

              <button
                className={"ml-1 " + (hasNextPage ? "" : " opacity-50")}
                onClick={() => nextPage()}
                disabled={!hasNextPage}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="1em"
                  height="1em"
                  viewBox="0 0 56 56"
                >
                  <path
                    fill="currentColor"
                    d="M18.695 48.367c.586 0 1.102-.234 1.524-.633L38.78 29.57c.446-.445.68-.984.68-1.57c0-.61-.234-1.172-.68-1.57L20.22 8.289a2.073 2.073 0 0 0-1.524-.656a2.122 2.122 0 0 0-2.156 2.156c0 .563.258 1.125.633 1.524L34.21 28L17.17 44.688c-.374.398-.632.937-.632 1.523c0 1.219.938 2.156 2.156 2.156"
                  ></path>
                </svg>
              </button>
              <button
                className={hasNextPage ? "" : " opacity-50"}
                onClick={() => setPageIndex(pageCount - 1)}
                disabled={!hasNextPage}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="1em"
                  height="1em"
                  viewBox="0 0 56 56"
                >
                  <path
                    fill="currentColor"
                    d="M29.149 48.356c.609 0 1.124-.235 1.523-.633l18.586-18.141c.445-.445.68-.984.68-1.57c0-.61-.258-1.196-.703-1.57L30.672 8.276a2.105 2.105 0 0 0-1.524-.632c-1.218 0-2.132.937-2.132 2.156c0 .562.234 1.125.61 1.523l17.062 16.688l-17.063 16.687c-.375.399-.61.938-.61 1.524c0 1.218.915 2.133 2.133 2.133m-20.93 0c.586 0 1.125-.235 1.523-.633l18.563-18.141c.445-.445.68-.984.68-1.57c0-.61-.235-1.196-.68-1.57L9.742 8.276c-.422-.398-.937-.632-1.523-.632A2.122 2.122 0 0 0 6.063 9.8c0 .562.257 1.125.632 1.523l17.04 16.688l-17.04 16.687c-.375.399-.632.938-.632 1.524c0 1.218.937 2.133 2.156 2.133"
                  ></path>
                </svg>
              </button>
            </div>
          </div>
        </>
      )}

      {includeSelection && (
        <>
          <span>
            Records ({selectedResults.length} out of {totalRows} selected)
          </span>

          <div className="flex items-center mt-2">
            <button
              className={buttonClasses}
              onClick={() => toggleAllRowsSelected()}
            >
              <svg
                className="h-5 w-5"
                xmlns="http://www.w3.org/2000/svg"
                width="1em"
                height="1em"
                viewBox="0 0 20 20"
              >
                <path fill="green" d="m0 11l2-2l5 5L18 3l2 2L7 18z"></path>
              </svg>
              <span className="hidden sm:block">
                {totalRows === selectedRowModel.flatRows.length
                  ? "Deselect All"
                  : "Select All"}
              </span>
            </button>
            <button
              onClick={() => {
                if (selectedRowModel.flatRows.length === totalRows) {
                  return;
                }

                removeUnselectedResults();
              }}
              className={buttonClasses}
            >
              <svg
                className="h-5 w-5"
                xmlns="http://www.w3.org/2000/svg"
                width="1em"
                height="1em"
                viewBox="0 0 24 24"
              >
                <path
                  fill="red"
                  d="M6.4 19L5 17.6l5.6-5.6L5 6.4L6.4 5l5.6 5.6L17.6 5L19 6.4L13.4 12l5.6 5.6l-1.4 1.4l-5.6-5.6z"
                ></path>
              </svg>
              <span className="hidden sm:block">Remove Unselected</span>
            </button>
            {/* <button
              className={`${buttonClasses} opacity-90 ${
                selectedResults.length === 0 ? "" : "hidden"
              }`}
              title="Please select records to access reports"
            >
              <svg
                className="h-5 w-5"
                xmlns="http://www.w3.org/2000/svg"
                width="1em"
                height="1em"
                viewBox="0 0 24 24"
              >
                <path
                  fill="blue"
                  d="M13 9h5.5L13 3.5zM6 2h8l6 6v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4c0-1.11.89-2 2-2m1 18h2v-6H7zm4 0h2v-8h-2zm4 0h2v-4h-2z"
                ></path>
              </svg>
              Reports
            </button> */}
            <div className={"dropdown dropdown-top"}>
              <div tabIndex={0} role={"button"} className={buttonClasses}>
                <svg
                  className="h-5 w-5"
                  xmlns="http://www.w3.org/2000/svg"
                  width="1em"
                  height="1em"
                  viewBox="0 0 24 24"
                >
                  <path
                    fill="blue"
                    d="M13 9h5.5L13 3.5zM6 2h8l6 6v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4c0-1.11.89-2 2-2m1 18h2v-6H7zm4 0h2v-8h-2zm4 0h2v-4h-2z"
                  ></path>
                </svg>
                <span className="hidden sm:block">Reports</span>
              </div>
              <ul
                tabIndex={0}
                className="dropdown-content menu bg-base-100 rounded-box z-[1] w-52 p-1 shadow"
              >
                {reports.map((report) => (
                  <li
                    key={report.label}
                    onClick={(e) => {
                      const elem = document.activeElement as HTMLElement;
                      if (elem) {
                        elem.blur();
                      }
                      report.action();
                    }}
                  >
                    <a>{report.label}</a>
                  </li>
                ))}
              </ul>
            </div>
            <button onClick={exportSelectedResults} className={buttonClasses}>
              <svg
                className="h-5 w-5"
                xmlns="http://www.w3.org/2000/svg"
                width="1em"
                height="1em"
                viewBox="0 0 48 48"
              >
                <g
                  fill="none"
                  stroke="green"
                  strokeLinecap="round"
                  strokeWidth={4}
                >
                  <path
                    strokeLinejoin="round"
                    d="M8 15V6a2 2 0 0 1 2-2h28a2 2 0 0 1 2 2v36a2 2 0 0 1-2 2H10a2 2 0 0 1-2-2v-9"
                  ></path>
                  <path d="M31 15h3m-6 8h6m-6 8h6"></path>
                  <path
                    strokeLinejoin="round"
                    d="M4 15h18v18H4zm6 6l6 6m0-6l-6 6"
                  ></path>
                </g>
              </svg>
              <span className="hidden sm:block">Export</span>
            </button>
          </div>
        </>
      )}
    </>
  );
}

export default ActionButtons;
